<template lang="pug">

    div

        // Headline
        .ui.attached.segment
            h2.ui.header
                i.tasks.icon
                .content {{ product.name }}

        .ui.secondary.attached.segment
            product-summary(
                v-bind:product="product"
                v-bind:campaign="campaign"
                v-bind:company="company"
                v-bind:mediaProductionPerson="mediaProductionPerson"
                v-bind:type="type"
            )

        .ui.bottom.attached.segment

            // Sub-Navi des Produkts
            sub-navigation(v-bind:product-model='product' @toggle="$emit('toggle')")

            //- Varianten-Navigation
            variant-navigation(
                v-if='variantNavigationPosition === "TOP"'
                v-bind:active-variant='activeVariant'
                v-bind:field-models='fieldModels'
                v-bind:form-model='formModel'
                v-bind:product='product'
                v-bind:variants='variants'
            )

            //- Embedded view
            router-view

            //- Varianten-Navigation
            variant-navigation(
                v-if='variantNavigationPosition === "BOTTOM"'
                v-bind:active-variant='activeVariant'
                v-bind:field-models='fieldModels'
                v-bind:form-model='formModel'
                v-bind:product='product'
                v-bind:variants='variants'
            )

            //- Footer
            .ui.basic.segment
                .ui.three.column.grid

                    //- Allgemeine Informationen & Unternehmensnotiz
                    .column

                        h5 Allgemeine Informationen
                        general-information(
                            v-bind:agency-models='agencies'
                            v-bind:campaign-models='campaigns'
                            v-bind:company-model='company'
                            v-bind:person-models='persons'
                            v-bind:product-model='product'
                        )

                    //- Variantendetails
                    .column

                        h5 Variantendetails
                        variant-details(
                            v-bind:label-width="50"
                            v-bind:field-models='fieldModels'
                            v-bind:form-model='formModel'
                            v-bind:variant-model='activeVariant'
                        )

                    //- Proof & Bemerkungen zum Produkt
                    .column
                        proof-details(
                            v-bind:proof-model='proofModel'
                            v-bind:shipping-address-models='shippingAddressModels'
                        )

</template>

<script>
    import Vue from 'vue'
    import * as R from 'ramda'
    import store from '@/store'
    import { type_id_to_form_id } from '@/utils/form'
    import GeneralInformation from '@/components/products/GeneralInformation'
    import NoticeField from '@/components/products/view/ProductNote'
    import ProductSummary from '@/components/products/Summary'
    import ProofDetails from '@/components/products/view/ProofDetails'
    import SubNavigation from '@/components/products/SubNavigation'
    import VariantDetails from '@/components/products/VariantDetails'
    import VariantNavigation from '@/components/products/VariantNavigation'

    //noinspection JSUnusedGlobalSymbols,JSUnusedLocalSymbols
    export default {

        async beforeRouteEnter(to, from, next) {
            const productId = Number(to.params.id)
            const variantId = Number(to.params.variantId)
            const companyId = Vue.$auth.companyId

            const [ productModel, companyModel ] = await Promise.all([

                // Produkt laden
                store.dispatch('products/loadOne', { id: productId }),

                // Unternehmen laden
                store.dispatch('companies/loadOne', { id: companyId }),

                // Alle eigenen Benutzer laden
                store.dispatch('users/loadAll'),

                // Alle eigenen Personen laden
                store.dispatch('persons/loadAll'),

                // Benutzer des Produkts laden
                store.dispatch('users/loadAllByProductId', { productId }),

                // Personen des Produkts laden laden
                store.dispatch('persons/loadAllByProductId', { productId }),

                // Varianten laden
                store.dispatch('variants/loadAllByProductId', { productId }),

                // Agenturen laden
                store.dispatch('companies/loadAllByIsAgency'),

                // Felder der Variante laden
                store.dispatch('fields/loadAllByVariantId', { variantId })

            ])

            const [ billindAddressModel ]  = await Promise.all([

                // Standard-Rechnungsadresse des Unternehmens laden
                store.dispatch('addresses/loadOne', { id: companyModel.defaultBillingAddress }),

                // Lieferadressen laden
                store.dispatch('addresses/loadAllByCompanyIdAndIsShippingAddress', { companyId: companyModel.id }),

                // Produkt-Typ laden
                store.dispatch('productTypes/loadOne', { id: productModel.type }),

                // Formular laden
                store.dispatch('forms/loadOne', { id: type_id_to_form_id(productModel.type) }),

                // Kampagnen laden
                store.dispatch('campaigns/loadAllByCompanyId', { companyId: productModel.company }),

                // ggf. Proof laden
                R.when(
                    R.is(Number),
                    proofId => store.dispatch('proofs/loadOne', { id: proofId }),
                    productModel.proof
                )

            ])

            await Promise.all([

                // Land der Rechnunsadresse laden
                store.dispatch('states/loadAllByCountry', { countryId: billindAddressModel.country }),

                // Bundeslaender der Adresse laden
                store.dispatch('states/loadAllByCountry', { countryId: billindAddressModel.country })

            ])

            next()
        },

        async beforeRouteUpdate(to, from, next) {
            const variantId = to.params.variantId

            await Promise.all([

                // Variante laden
                this.$store.dispatch('variants/loadOne', { id: variantId }),

                // Felder der Variante laden
                store.dispatch('fields/loadAllByVariantId', { variantId })

            ])

            next()
        },

        components: {
            GeneralInformation, NoticeField, ProductSummary, ProofDetails, SubNavigation, VariantDetails,
            VariantNavigation
        },

        computed: {

            activeVariant() {
                return R.find(R.propEq('id', this.variantId), this.variants)
            },

            agencies() {
                return store.getters['companies/allByIsAgencyOfCompanyId'](this.company.id)
            },

            billingAddressModel() {
                return R.compose(

                    // Adresse holen
                    store.getters['addresses/byId'],

                    // ID der Standard-Rechnungsadresse lesen
                    R.prop('defaultBillingAddress')

                )(this.company)
            },

            campaign() {
                return R.cond([

                    // Default-Wert
                    [R.isNil, R.always({})],

                    // Kampagne anhand von ID holen
                    [R.T, store.getters['campaigns/byId']],

                ])(this.product.campaign)
            },

            campaigns() {
                return store.getters['campaigns/allByCompanyId'](this.company.id)
            },

            company() {
                return store.getters['companies/byId'](this.product.company)
            },

            companyUserModels() {
                return store.getters['users/allByCompanyId'](this.product.company)
            },

            fieldModels() {
                return store.getters['fields/allByVariantId'](this.variantId)
            },

            formModel() {
                return this.$store.getters['forms/byId'](type_id_to_form_id(this.product.type))
            },

            jobs() {
                return this.$store.getters['jobs/allByProductId'](this.product.id)
            },

            jobModelsByVariant() {
                return R.compose(

                    // Jobs anhand von ID holen
                    R.map(R.find(R.__, this.jobs)),

                    // Predicate fuer Job-Suche erstellen
                    R.map(R.propEq('id')),

                    // Doppelungen in Job-IDs entfernen
                    R.uniq,

                    // Job-IDs aus Angeboten lesen
                    R.map(R.prop('job'))

                )(this.offerModelsByActiveVariant)

            },

            offerModelsByActiveVariant() {
                return R.filter(R.propEq('variant', this.activeVariant.id), this.offers)
            },

            mediaProductionPerson() {
                const user = this.$store.getters['users/byId'](this.product.mediaProduction)
                return this.$store.getters['persons/byId'](user.person)
            },

            offers() {
                return R.compose(

                    // flatten
                    R.flatten,

                    // Angebote zu Job-IDs holen
                    R.map(store.getters['offers/allByJobId']),

                    // Job-IDs auslesen
                    R.map(R.prop('id'))

                )(this.jobs)
            },

            product() {
                return store.getters['products/byId'](this.productId)
            },

            proofModel() {
                return store.getters['proofs/byId'](this.product.proof)
            },

            persons() {
                return store.getters['persons/all']
            },

            productId() {
                return parseInt(this.$route.params.id)
            },

            shippingAddressModels() {
                return store.getters['addresses/allByCompanyIdAndIsShippingAddress'](this.company.id)
            },

            suppliers() {
                return this.$store.getters['companies/allByIsSupplier']
            },

            type() {
                return store.getters['productTypes/byId'](this.product.type)
            },

            variantNavigationPosition() {
                return this.$route.meta.variantNavigationPosition || 'TOP'
            },

            variantId() {
                return Number(this.$route.params.variantId)
            },

            variants() {
                return store.getters['variants/allByProductId'](this.productId)
            },

        },

    }
</script>
